// ============================================
// ALPHA OPTIONS - GLOBAL TYPES (FINAL SYNC)
// ============================================

export interface TickerAnalysis {
  ticker: string;
  currentPrice: number;
  analyzedAt: string;
  scores: {
    total: number;
    normalizedTotal: number;
    step1_sentiment: StepScore;
    step2_technicals: StepScore;
    step3_options: StepScore;
    step4_risk: StepScore;
    step5_greeks: StepScore;
    step6_entry: StepScore;
    step7_review: StepScore;
    step8_sizing: StepScore;
  };
  positionSizing: PositionSizing;
  riskFlags: RiskFlags;
  marketData: MarketData;
  technicals: TechnicalData;
  optionsData: OptionsData;
  strategies: StrategyRecommendation[];
  bestStrategy: StrategyRecommendation | null;
  tradePlan: TradePlan | null;
  volMetrics?: VolatilityMetrics;   // Quantitative volatility analysis
  status: 'TRADE' | 'NO_TRADE' | 'CAUTION';
  statusReason: string;
}

export interface StepScore {
  points: number;
  maxPoints: number;
  details: string[];
  passed: boolean;
}

export interface PositionSizing {
  accountSize: number;
  riskTolerancePercent: number;
  riskToleranceDollars: number;
  maxRiskDollars: number;
  vixAdjustment: number;
  adjustedMaxRisk: number;
  riskPerContract: number;
  maxContracts: number;
  recommendedContracts: number;
  capitalRequired: number;
  canAfford: boolean;
  sizeWarnings: string[];
  portfolioPercent?: number; 
  currentExposure?: number;  
}

export interface RiskFlags {
  earningsInDays: number | null;
  earningsPenalty?: number;
  fomcInDays: number | null;
  fomcPenalty?: number;
  liquidityPass: boolean;
  liquidityGrade: string;
  liquidityDetails?: string;
  ivRank: number;
  ivStrategy: "BUY_PREMIUM" | "SELL_PREMIUM" | "NEUTRAL";
  totalPenalty?: number;
  warningFlags: string[];
  criticalFlags: string[];
  autoReject: boolean;
  autoRejectReason?: string | null;

  // Extra fields
  earningsRisk?: string;
  fomcRisk?: string;
  vixLevel?: number;  // VIX level as number for calculations
  dayAfterFomc?: boolean;
  emaConfirmed?: boolean;
  fomcBias?: 'BEARISH' | 'NEUTRAL';

  // v4.0: Enhanced Risk Fields
  sectorCatalyst?: {
    type: 'FDA' | 'PATENT' | 'REGULATORY' | 'COMPETITOR_EARNINGS' | 'ECONOMIC' | 'NONE';
    daysUntil: number | null;
    description?: string;
    penalty: number;
  };
  vixSizeMultiplier?: number;    // 0.25-1.0 based on VIX level
}

export interface MarketData {
  price: number;
  change: number;
  changePercent: number;
  volume: number;
  prevClose?: number;
  vixLevel: "LOW" | "NORMAL" | "HIGH" | "ELEVATED" | "CRISIS";
  vix?: number;
  avgVolume: number;
  marketCap: number;
  sector: string;
  sectorPerformance: number;
  dividendYield: number;
  earningsDate: string;
  peRatio?: number;
  beta?: number;
  high52?: number;
  low52?: number;
  spyTrend?: 'BULLISH' | 'BEARISH' | 'NEUTRAL';
  dataSource?: string;
  error?: string;
  fetchedAt?: string;  // ISO timestamp when data was fetched
  isDelayed?: boolean; // True if data source has delays (e.g., Finnhub free tier)
}

export interface TechnicalData {
  rsi: number;
  macd: {
    value: number;
    signal: number;
    histogram: number;
    crossover: "NONE" | "BULLISH" | "BEARISH";
  };
  adx?: number;
  atr?: number;
  ema8: number;
  ema21: number;
  ema34: number;
  ema55: number;
  sma200: number;
  emaAlignment?: 'BULLISH' | 'BEARISH' | 'MIXED';
  priceAboveEma21: boolean;
  daysAboveEma21: number;
  atrTrending: boolean;
  bias: string;
  support: number;
  resistance: number;
  volumeSma: number;
  squeeze: boolean;
  impulse: "RED" | "GREEN" | "BLUE";
  obv: number;
  pivotPoints: { p: number; r1: number; s1: number; };
  high52?: number;
  low52?: number;
  beta?: number;
  peRatio?: number;
  dataSource?: string;

  // v4.0: Volatility Compression Fields
  bbWidth?: number;              // Bollinger Band Width
  bbWidth10Pct?: number;         // 10th percentile BBW over 252 days
  ribbonStdDev?: number;         // MA ribbon standard deviation
  ribbonStdDev5Pct?: number;     // 5th percentile ribbon std over 252 days
  kcUpper?: number;              // Keltner Channel upper
  kcLower?: number;              // Keltner Channel lower
  bbUpper?: number;              // Bollinger Band upper
  bbLower?: number;              // Bollinger Band lower
  isSqueezing?: boolean;         // BB inside KC
  squeezeReleased?: boolean;     // Squeeze just fired
  coilScore?: number;            // Computed coil score (0-10)
  volumeRatio?: number;          // Current volume / 20-day avg
  atr20DayAvg?: number;          // 20-day average ATR for expansion detection
}

export interface OptionContract {
  strike: number;
  type: "CALL" | "PUT";
  bid: number;
  ask: number;
  last: number;
  volume: number;
  openInterest: number;
  impliedVolatility: number;
  delta: number;
  gamma: number;
  theta: number;
  vega: number;
  expiration: string;
  dte: number;
}

export interface OptionsData {
  ivRank: number;
  ivSignal: "LOW" | "NORMAL" | "HIGH" | "EXTREME";
  currentIV: number;
  ivStrategy: "BUY_PREMIUM" | "SELL_PREMIUM" | "NEUTRAL";
  liquidityGrade: "A" | "B" | "C" | "D" | "F";
  bidAskPercent: number;
  totalOpenInterest: number;
  callVolume: number;
  putVolume: number;
  ivPercentile: number;
  historicalIV: number;
  expectedMoveExceedsHistorical: boolean;
  expectedMoveRatio: number;
  putCallRatio: number;
  gammaExposure: number;
  volumeOpenInterestRatio?: number;
  chain?: OptionContract[];
  dataSource?: string;
  synthetic?: boolean;
}

export interface StrategyLeg {
  action: "BUY" | "SELL";
  amount: number;
  type: "CALL" | "PUT";
  strike: number;
  expiry: string;
  dte: number;
  delta: number;
  bid: number;
  ask: number;
  mid?: number;
  openInterest: number;
  theta?: number; 
  gamma?: number;
  vega?: number;
  iv?: number;
  volume?: number; // Added
}

export type OptionLeg = StrategyLeg; 

export interface StrategyGreeks {
  delta: number;
  theta: number;
  gamma: number;
  vega: number;
  deltaIdeal: string;
  vegaIdeal: string;
  thetaIdeal?: string;
  gammaIdeal?: string; // Added
  thetaDailyDollar: number;
  deltaDollarExposure?: number;
  gammaWithinLimit?: boolean; 
  deltaWithinLimit?: boolean;
}

export interface TradeManagement {
  profitTarget50: number;
  profitTarget25: number;
  maxLossMultiple: number;
  stopLossPrice: number;
  dte21Checkpoint: boolean;
  dte21Action: string;
  dte14GammaWarning: boolean;
  adjustmentTriggers: {
    deltaThreshold: number;
    lossThreshold: number;
    underlyingBreak: boolean;
  };
  hasDefinedExit: boolean;
  hasStopLoss: boolean;
  hasProfitTarget: boolean;
  hasTimeExit: boolean;
  managementScore: number;
}

export interface StrategyRecommendation {
  id: string;
  name: string;
  type: string;
  direction: "BULLISH" | "BEARISH" | "NEUTRAL";
  suitabilityScore: number;
  suitabilityReasons: string[];
  riskProfile: "DEFINED" | "UNDEFINED" | "AGGRESSIVE" | "SEMI_DEFINED";
  creditDebit: "CREDIT" | "DEBIT";
  legs: StrategyLeg[];
  maxProfit: number;
  maxLoss: number;
  breakeven: number | number[];
  probabilityOfProfit: number;
  riskRewardRatio: number;
  greeks: StrategyGreeks;
  warnings: string[];
  premiumToWidth?: number | null;
  management?: TradeManagement;
  // Enhanced fields for better explanations
  expiryReasoning?: string;       // Why this expiry was chosen
  strategyExplanation?: string;   // Full explanation of the strategy
  directionReasoning?: string;    // Why bullish/bearish/neutral
}

export interface TradePlan {
  entryPrice: number;
  
  // Complex Unions
  stopLoss: number | {
    price: number;
    condition: string;
    lossAmount: number;
    multiple: number;
  };
  takeProfit: number | {
    price: number;
    percent: number;
    action: string;
  };
  
  entryType?: 'LIMIT' | 'MARKET';
  entryCondition?: string;
  contracts: number;
  maxContracts: number;
  capitalRequired: number;
  maxRiskDollars: number;
  portfolioPercent?: number;
  riskPerContract: number;
  
  // Optional Objects
  profitTargets?: {
     target50: { price: number; percent: 50; action: string };
     target85: { price: number; percent: 85; action: string };
     targetFull: { price: number; percent: 100; action: string };
  };
  timeManagement?: {
    dte21Check: { date: string; action: string };
    dte14Warning: { date: string; action: string };
    finalExit: { date: string; dte: number; action: string };
  };
  adjustmentTriggers?: string[];
  checklist?: string[];
  rationale: string;
}

export interface UserTier {
  type: 'BASIC' | 'PRO' | 'ELITE';
  analysesPerDay: number;
  analysesToday: number;
  canAnalyze: boolean;
}

// ============================================
// v4.0: VOLATILITY COMPRESSION & TRAILING STOPS
// ============================================

export interface CoilScore {
  total: number;                 // 0-10 composite score
  bbWidthSignal: number;         // 0-3 pts for BB width compression
  ribbonSignal: number;          // 0-3 pts for MA ribbon compression
  kcSqueezeSignal: number;       // 0-2 pts for Keltner squeeze
  crossoverSignal: number;       // 0-2 pts for post-squeeze crossover
  interpretation: 'TERMINAL_CONSOLIDATION' | 'MODERATE_COMPRESSION' | 'NO_COMPRESSION';
  breakoutProbability: number;   // Estimated 0-100%
}

export interface TrailingStopConfig {
  enabled: boolean;
  initialMultiple: number;       // ATR multiple at entry (e.g., 1.5)
  profitLevels: {
    profitPercent: number;       // e.g., 25, 50, 100, 150
    atrMultiple: number;         // Tighten to this multiple
  }[];
  currentLevel: number;          // Current profit % for tracking
  currentStopPrice: number;      // Calculated trailing stop price
  atrValue: number;              // Current ATR for calculation
}

export interface TieredExitConfig {
  tier1: { percent: number; exitPercent: number };  // e.g., { percent: 50, exitPercent: 30 }
  tier2: { percent: number; exitPercent: number };  // e.g., { percent: 100, exitPercent: 30 }
  runner: { trailWithATR: boolean; atrMultiple: number };
}

export interface MomentumOverride {
  shouldHold: boolean;
  reasons: string[];
  rsiAligned: boolean;
  macdAligned: boolean;
  volumeConfirmed: boolean;
}

// v4.0 Enhanced earnings thresholds
export const EARNINGS_THRESHOLDS_V4 = {
  autoReject: 5,           // ≤5 days = AUTO NO_TRADE
  severe: 7,               // ≤7 days = -18 pts
  moderate: 10,            // ≤10 days = -12 pts
  minor: 14,               // ≤14 days = -8 pts
  catalystWindowStart: 14, // 14-56 days = BONUS
  catalystWindowEnd: 56,
  catalystBonus: 5         // +5 pts in catalyst window
};

// v4.0 VIX-based position sizing multipliers
export const VIX_SIZE_MULTIPLIERS = {
  calm: { max: 15, multiplier: 1.0 },
  normal: { max: 20, multiplier: 0.9 },
  elevated: { max: 25, multiplier: 0.75 },
  high: { max: 30, multiplier: 0.5 },
  extreme: { max: Infinity, multiplier: 0.25 }
};

export const FOMC_DATES_2025_2026 = [
  // 2025 FOMC meetings (statement dates - second day of each meeting)
  "2025-01-29", "2025-03-19", "2025-05-07", "2025-06-18",
  "2025-07-30", "2025-09-17", "2025-10-29", "2025-12-10",
  // 2026 FOMC meetings (statement dates - second day of each meeting)
  "2026-01-28", "2026-03-18", "2026-04-29", "2026-06-17",
  "2026-07-29", "2026-09-16", "2026-10-28", "2026-12-09"
];

// ============================================
// POSITION TRACKER SYSTEM
// ============================================

export type PositionStatus = 'ACTIVE' | 'CLOSED_PROFIT' | 'CLOSED_LOSS' | 'CLOSED_MANUAL' | 'EXPIRED';

export interface ActivePosition {
  id?: number;
  userId?: number;
  clerkId?: string;

  // Trade identifiers
  ticker: string;
  strategyName: string;
  strategyType: string;
  direction: 'BULLISH' | 'BEARISH' | 'NEUTRAL';
  creditDebit: 'CREDIT' | 'DEBIT';

  // Position details
  legs: StrategyLeg[];
  contracts: number;
  entryPrice: number;        // Credit received or debit paid per contract
  entryDate: string;         // ISO timestamp
  expirationDate: string;    // Options expiration

  // Risk/reward
  maxProfit: number;
  maxLoss: number;
  breakeven: number | number[];

  // Tracking targets
  stopLossPrice: number;
  profitTarget50: number;     // 50% of max profit price
  profitTarget100: number;    // Full profit price

  // Current state
  currentPrice?: number;      // Current mid price of the spread
  currentPnL?: number;        // Current P&L in dollars
  currentPnLPercent?: number; // Current P&L as % of max profit
  lastPriceUpdate?: string;   // ISO timestamp of last price check

  // ATR-based trailing stop
  atrValue?: number;          // ATR at entry for trailing calculations
  trailingStopPrice?: number; // Dynamic trailing stop (tightens as profit grows)
  trailingStopEnabled: boolean;

  // Alerts
  alertsEnabled: boolean;
  emailAlertsEnabled: boolean;
  lastAlertSent?: string;     // ISO timestamp to prevent spam
  alertCooldownMinutes: number; // Min time between alerts (default 30)

  // Status
  status: PositionStatus;
  closedAt?: string;
  closedPrice?: number;
  closedPnL?: number;
  closeReason?: string;

  // Notes
  notes?: string;

  // Timestamps
  createdAt?: string;
  updatedAt?: string;
}

export interface PositionAlert {
  id?: number;
  positionId: number;
  alertType: 'STOP_HIT' | 'PROFIT_50' | 'PROFIT_100' | 'DTE_WARNING' | 'TRAILING_STOP' | 'PRICE_TARGET';
  message: string;
  triggered: boolean;
  triggeredAt?: string;
  acknowledged: boolean;
  sentViaEmail: boolean;
  createdAt?: string;
}

export interface PositionSummary {
  totalPositions: number;
  activePositions: number;
  totalPnL: number;
  totalWins: number;
  totalLosses: number;
  winRate: number;
  avgWin: number;
  avgLoss: number;
  payoffRatio: number;
}

export const TOTAL_POSSIBLE_POINTS = 145;
export const SCORING_WEIGHTS = {
  step1_sentiment: 15,
  step2_technicals: 20,
  step3_options: 15,
  step4_risk: 25,
  step5_greeks: 20,
  step6_entry: 25,
  step7_review: 10,
  step8_sizing: 15
};

export const RISK_THRESHOLDS = {
  score: { tradeThreshold: 70, cautionThreshold: 60 },
  iv: { high: 50, extreme: 80 },
  liquidity: { minOI: 1000, maxSpread: 5.0 }
};

// === QUANTITATIVE ENGINE TYPES (Medallion-inspired) ===

// Historical price bar for volatility calculations
export interface PriceBar {
  date: string;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

// Market regime classification (simplified HMM replacement)
export type RegimeState = 'HIGH_VARIANCE' | 'TRENDING' | 'MEAN_REVERTING' | 'COMPRESSING' | 'STABLE';

export interface MarketRegime {
  state: RegimeState;
  confidence: number;        // 0-1 confidence in state classification
  volRatio: number;          // shortVol / longVol ratio
  trendStrength: number;     // consecutive days in trend direction
  trendDirection: 'UP' | 'DOWN' | 'NONE';
  description: string;       // human-readable regime description
}

// Volatility metrics (Sinclair method)
export interface VolatilityMetrics {
  // Realized volatility across windows (annualized)
  rv10d: number;             // 10-day close-to-close RV
  rv20d: number;             // 20-day close-to-close RV
  rv40d: number;             // 40-day RV
  rv60d: number;             // 60-day RV
  parkinsonRV20d: number;    // 20-day Parkinson (high-low) RV — more accurate

  // EWMA forecast (GARCH approximation, λ=0.94)
  ewmaForecast: number;      // next-period forecasted vol (annualized)

  // The Edge: IV vs RV (Sinclair's core profit signal)
  currentIV: number;         // from options chain
  volEdge: number;           // IV - EWMA forecast (positive = sell premium)
  volEdgePercent: number;    // edge as % of IV
  edgeSignal: 'SELL_PREMIUM' | 'BUY_PREMIUM' | 'NEUTRAL';
  edgeStrength: 'STRONG' | 'MODERATE' | 'WEAK' | 'NONE';

  // Mean reversion z-score
  zScore20d: number;         // (price - mean20d) / stddev20d
  zScoreSignal: string;      // human-readable interpretation

  // Market regime
  regime: MarketRegime;

  // Kelly criterion sizing
  kellyFraction: number;     // raw Kelly fraction
  halfKelly: number;         // half-Kelly (safety constraint)
  kellyContracts: number;    // contracts derived from Kelly

  // Data quality
  dataPoints: number;        // how many historical bars used
  dataSource: string;        // 'alphavantage' | 'synthetic'
}

// Context passed from Scanner → Moonshot via React Router state
export interface ScannerContext {
  ticker: string;
  currentPrice: number;
  score: number;
  status: 'TRADE' | 'NO_TRADE' | 'CAUTION';
  statusReason: string;
  direction: 'BULLISH' | 'BEARISH' | 'NEUTRAL';
  strategyName: string;
  legs: StrategyLeg[];
  maxProfit: number;
  maxLoss: number;
  breakeven: number | number[];
  probabilityOfProfit: number;
  riskRewardRatio: number;
  creditDebit: 'CREDIT' | 'DEBIT';
  warnings: string[];
  ivRank: number;
  riskFlags: string[];

  // Quantitative context (from volatility engine)
  stepScores?: {
    step1_sentiment: { score: number; max: number; passed: boolean };
    step2_technicals: { score: number; max: number; passed: boolean };
    step3_options: { score: number; max: number; passed: boolean };
    step4_risk: { score: number; max: number; passed: boolean };
    step5_greeks: { score: number; max: number; passed: boolean };
    step6_entry: { score: number; max: number; passed: boolean };
    step7_review: { score: number; max: number; passed: boolean };
    step8_sizing: { score: number; max: number; passed: boolean };
  };
  volEdge?: number;              // IV - Realized Vol edge (positive = sell premium)
  volEdgeSignal?: 'SELL_PREMIUM' | 'BUY_PREMIUM' | 'NEUTRAL';
  volEdgeStrength?: 'STRONG' | 'MODERATE' | 'WEAK' | 'NONE';
  regime?: RegimeState;          // Market regime classification
  regimeConfidence?: number;     // 0-1 confidence in regime
  zScore?: number;               // Mean reversion z-score
  ewmaForecast?: number;         // EWMA forecasted volatility
  rv20d?: number;                // 20-day realized volatility
  kellyContracts?: number;       // Kelly-derived contract count
  analyzedAt?: string;           // ISO timestamp of scanner analysis
}

// ============================================
// RESEARCH ENGINE TYPES
// ============================================

export type MispricingTag = 'UNDERVALUED' | 'OVERVALUED' | 'FAIR';

export interface UniverseStock {
  ticker: string;
  name: string;
  optionsVolume: number;          // Daily options volume
  avgSpread: number;              // Avg bid-ask spread %
  marketCap: number;
  sector: string;
  liquidityScore: number;         // 0-100 composite liquidity
  source: string;                 // Where we found it (barchart, manual, etc.)
}

export interface MispricingAnalysis {
  ticker: string;
  tag: MispricingTag;
  score: number;                  // -10 (very overvalued) to +10 (very undervalued)
  confidence: number;             // 0-100 confidence in mispricing assessment
  signals: {
    pricePosition52w: number;     // 0-1 where in 52w range
    rsiLevel: number;
    ivRankLevel: number;
    peRatio?: number;
    sectorComparison?: string;    // vs sector avg
  };
  reasoning: string[];            // Human-readable reasons
}

export interface ResearchResult {
  id?: number;
  ticker: string;
  name?: string;

  // Mispricing data
  mispricingTag: MispricingTag;
  mispricingScore: number;
  mispricingReasoning: string[];

  // 7-step analysis results
  tradeScore: number;             // Normalized 0-100
  status: 'TRADE' | 'NO_TRADE' | 'CAUTION';
  statusReason: string;

  // Best strategy
  strategyName: string;
  strategyType: string;
  direction: 'BULLISH' | 'BEARISH' | 'NEUTRAL';
  creditDebit: 'CREDIT' | 'DEBIT';

  // Risk/reward
  maxProfit: number;
  maxLoss: number;
  breakeven: number | number[];
  probabilityOfProfit: number;
  riskRewardRatio: number;

  // Key metrics for ranking
  ivRank: number;
  liquidityGrade: string;
  daysToEarnings: number | null;

  // Position sizing
  recommendedContracts: number;
  capitalRequired: number;

  // Management guidance
  managementNotes: string[];

  // Full analysis (for drill-down)
  fullAnalysis?: TickerAnalysis;

  // Metadata
  analyzedAt: string;
  rank?: number;
}

export interface ResearchRun {
  id?: number;
  userId?: number;
  runName: string;

  // Universe info
  universeSource: 'manual' | 'watchlist' | 'most_active' | 'sector' | 'ai_screened';
  universeCount: number;
  tickersInUniverse: string[];

  // Filtering criteria
  minLiquidityScore?: number;
  mispricingFilter?: MispricingTag[];
  sectorFilter?: string[];

  // Results
  tickersAnalyzed: number;
  tickersPassed: number;          // Passed liquidity + mispricing thresholds
  results: ResearchResult[];
  topTen: ResearchResult[];       // Top 10 ranked ideas

  // Status
  status: 'queued' | 'running' | 'completed' | 'failed' | 'cancelled';
  progress: number;               // 0-100
  currentTicker?: string;         // Currently analyzing

  // Timestamps
  createdAt: string;
  startedAt?: string;
  completedAt?: string;

  // Errors
  errors?: { ticker: string; error: string }[];
}

export interface ResearchEngineConfig {
  // Universe settings
  universeSource: 'manual' | 'watchlist' | 'most_active' | 'sector';
  manualTickers?: string[];
  sectors?: string[];

  // Filtering
  minLiquidityScore: number;      // 0-100, default 50
  minOptionsVolume: number;       // Minimum daily options volume
  maxSpread: number;              // Max bid-ask spread %

  // Mispricing
  mispricingThreshold: number;    // Abs value score threshold (e.g., 3)
  includeTags: MispricingTag[];   // Which tags to include

  // Analysis
  riskTolerance: 5 | 10;
  maxConcurrentAnalysis: number;  // Rate limit protection

  // Output
  maxResults: number;             // Max results to return (default 10)
  sortBy: 'score' | 'mispricing' | 'probability' | 'riskReward';
}

// Default liquid universe (known options-active stocks)
export const DEFAULT_LIQUID_UNIVERSE: string[] = [
  // Mega-cap tech
  'AAPL', 'MSFT', 'GOOGL', 'AMZN', 'META', 'NVDA', 'TSLA',
  // Indices
  'SPY', 'QQQ', 'IWM', 'DIA',
  // High-volume financials
  'JPM', 'BAC', 'WFC', 'GS', 'MS', 'C',
  // Retail/Consumer
  'WMT', 'HD', 'TGT', 'COST', 'NKE',
  // Healthcare/Pharma
  'JNJ', 'PFE', 'ABBV', 'MRK', 'LLY', 'UNH',
  // Energy
  'XOM', 'CVX', 'OXY', 'SLB',
  // Industrials
  'CAT', 'DE', 'BA', 'UPS', 'FDX',
  // Semiconductors
  'AMD', 'INTC', 'MU', 'QCOM', 'AVGO',
  // Other high-volume
  'DIS', 'NFLX', 'PYPL', 'SQ', 'COIN', 'UBER', 'ABNB'
];
